import { useCallback, useState } from 'react';
import { Avatar, Box, ButtonBase, Stack, Typography } from '@mui/material';
import { AccountPopover } from './account-popover';
import { useAuthContext } from '../contexts/auth-context';

interface AccountButtonProps {
	onLoadingChange?: (loading: boolean, error?: string) => void;
}

export const AccountButton = ({ onLoadingChange }: AccountButtonProps) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const { jwtDecoded } = useAuthContext();

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			setAnchorEl(event.currentTarget);
		},
		[]
	);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const handleShopChange = useCallback((error?: string) => {
		onLoadingChange?.(true, error);
	}, [onLoadingChange]);

	const handleShopChangeComplete = useCallback(() => {
		onLoadingChange?.(false);
	}, [onLoadingChange]);

	const initials = jwtDecoded?.email
		? `${jwtDecoded.email.charAt(0)}${jwtDecoded.email.charAt(1)}`.toUpperCase()
		: 'NA';

	return (
		<>
			<ButtonBase
				onClick={handleClick}
				sx={{
					alignItems: 'center',
					display: 'flex',
					borderRadius: 2,
					padding: '6px 12px',
					'&:hover': {
						backgroundColor: 'rgba(255, 255, 255, 0.1)'
					}
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center">
					<Avatar
						sx={{
							height: 32,
							width: 32,
							backgroundColor: 'primary.main',
							fontSize: '0.875rem'
						}}
					>
						{initials}
					</Avatar>
					<Stack spacing={0.5}>
						<Typography
							variant="subtitle2"
							sx={{
								color: 'text.primary',
								fontWeight: 600,
								lineHeight: 1
							}}
						>
							{jwtDecoded?.email?.split('@')[0]}
						</Typography>
						<Typography
							variant="caption"
							sx={{
								color: 'text.secondary',
								lineHeight: 1
							}}
						>
							{jwtDecoded?.email?.split('@')[1]}
						</Typography>
					</Stack>
				</Stack>
			</ButtonBase>
			<AccountPopover
				anchorEl={anchorEl}
				onClose={handleClose}
				open={Boolean(anchorEl)}
				onShopChange={handleShopChange}
				onShopChangeComplete={handleShopChangeComplete}
			/>
		</>
	);
};
